// Admin Panel Cloud Backup & Sync - Implementare completă
import React, { useState, useEffect } from 'react';
import {
  Cloud,
  HardDrive,
  RefreshCw,
  Shield,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Settings,
  Play,
  Pause,
  RotateCcw,
  Database,
  Server,
  Calendar,
  Eye,
  Download,
  Upload,
  Archive,
  Folder,
  File,
  Zap,
  Activity,
  Bell,
  Lock,
  Unlock,
  Monitor,
  Smartphone,
  Laptop,
  Globe,
  MapPin
} from 'lucide-react';

const CloudBackup = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isBackupRunning, setIsBackupRunning] = useState(false);
  const [lastBackupTime, setLastBackupTime] = useState('2025-01-15 23:30');

  // Mock data pentru backup-uri
  const [backupJobs] = useState([
    {
      id: 1,
      name: 'Backup Complet Sistem',
      type: 'full',
      schedule: 'Zilnic la 02:00',
      lastRun: '2025-01-15 02:00',
      nextRun: '2025-01-16 02:00',
      status: 'completed',
      size: '2.4 GB',
      duration: '25 min',
      retention: '30 zile'
    },
    {
      id: 2,
      name: 'Backup Documente',
      type: 'incremental',
      schedule: 'La fiecare 4 ore',
      lastRun: '2025-01-15 16:00',
      nextRun: '2025-01-15 20:00',
      status: 'running',
      size: '156 MB',
      duration: '3 min',
      retention: '90 zile',
      progress: 67
    },
    {
      id: 3,
      name: 'Backup Baze de Date',
      type: 'differential',
      schedule: 'Săptămânal - Duminica',
      lastRun: '2025-01-12 03:00',
      nextRun: '2025-01-19 03:00',
      status: 'scheduled',
      size: '890 MB',
      duration: '12 min',
      retention: '6 luni'
    },
    {
      id: 4,
      name: 'Backup Media Files',
      type: 'full',
      schedule: 'Lunar',
      lastRun: '2025-01-01 04:00',
      nextRun: '2025-02-01 04:00',
      status: 'error',
      size: '15.7 GB',
      duration: '2h 15min',
      retention: '1 an',
      error: 'Insufficient storage space'
    }
  ]);

  // Mock data pentru statistici
  const [stats] = useState({
    totalBackups: 247,
    successfulBackups: 238,
    totalSize: '127.4 GB',
    successRate: 96.4,
    lastWeekBackups: 45,
    avgBackupTime: '18 min',
    storageUsed: 78,
    retentionPolicy: '6 luni'
  });

  // Mock data pentru locații sync
  const [syncLocations] = useState([
    {
      id: 1,
      name: 'Amazon S3 - Primary',
      type: 'cloud',
      region: 'eu-west-1',
      status: 'connected',
      lastSync: '2025-01-15 16:45',
      files: 1247,
      size: '45.7 GB',
      icon: Cloud
    },
    {
      id: 2,
      name: 'Local Server - Office',
      type: 'local',
      region: 'Bucharest, RO',
      status: 'connected',
      lastSync: '2025-01-15 16:40',
      files: 1247,
      size: '45.7 GB',
      icon: Server
    },
    {
      id: 3,
      name: 'Google Drive - Secondary',
      type: 'cloud',
      region: 'europe-west3',
      status: 'warning',
      lastSync: '2025-01-15 14:20',
      files: 1156,
      size: '43.2 GB',
      icon: Cloud,
      warning: 'Sync delay detected'
    }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'running': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
      case 'scheduled': return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
      case 'error': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'paused': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const getStatusIcon = (status) => {
    const iconProps = { className: 'w-4 h-4' };
    
    switch (status) {
      case 'completed': return <CheckCircle2 {...iconProps} className="w-4 h-4 text-green-600" />;
      case 'running': return <RefreshCw {...iconProps} className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'scheduled': return <Clock {...iconProps} className="w-4 h-4 text-gray-600" />;
      case 'error': return <AlertTriangle {...iconProps} className="w-4 h-4 text-red-600" />;
      case 'paused': return <Pause {...iconProps} className="w-4 h-4 text-yellow-600" />;
      default: return <Clock {...iconProps} className="w-4 h-4 text-gray-600" />;
    }
  };

  const getBackupTypeColor = (type) => {
    switch (type) {
      case 'full': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'incremental': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'differential': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const runBackupNow = (jobId) => {
    setIsBackupRunning(true);
    // Simulate backup running
    setTimeout(() => {
      setIsBackupRunning(false);
      setLastBackupTime(new Date().toLocaleString());
    }, 5000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Backup & Sincronizare
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Protejează-ți datele cu backup-uri automate și sincronizare multi-cloud
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => runBackupNow(1)}
                disabled={isBackupRunning}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                {isBackupRunning ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {isBackupRunning ? 'Se execută...' : 'Backup Acum'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Archive className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalBackups}</p>
                <p className="text-xs text-gray-500">Total backup-uri</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <CheckCircle2 className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.successfulBackups}</p>
                <p className="text-xs text-gray-500">Finalizate</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <HardDrive className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSize}</p>
                <p className="text-xs text-gray-500">Spațiu total</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Zap className="w-8 h-8 text-green-500 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.successRate}%</p>
                <p className="text-xs text-gray-500">Rata succes</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.lastWeekBackups}</p>
                <p className="text-xs text-gray-500">Săptămâna trecută</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-blue-500 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.avgBackupTime}</p>
                <p className="text-xs text-gray-500">Timp mediu</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-8 h-8 mr-3">
                <div className="relative w-8 h-8">
                  <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 32 32">
                    <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-300" />
                    <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="4" fill="transparent" 
                            strokeDasharray={`${stats.storageUsed * 0.75} 75`} className="text-orange-500" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{stats.storageUsed}%</span>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Stocare</p>
                <p className="text-xs text-gray-500">utilizată</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-indigo-600 mr-3" />
              <div>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.retentionPolicy}</p>
                <p className="text-xs text-gray-500">Retenție</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Prezentare Generală', icon: Activity },
              { id: 'jobs', name: 'Job-uri Backup', icon: Archive },
              { id: 'sync', name: 'Sincronizare', icon: RefreshCw },
              { id: 'settings', name: 'Setări', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Backups */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Backup-uri Recente
              </h3>
              
              <div className="space-y-4">
                {backupJobs.slice(0, 4).map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center">
                      {getStatusIcon(job.status)}
                      <div className="ml-3">
                        <p className="font-medium text-gray-900 dark:text-white">{job.name}</p>
                        <p className="text-sm text-gray-500">
                          {job.lastRun} • {job.size}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBackupTypeColor(job.type)}`}>
                      {job.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sync Status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Status Sincronizare
              </h3>
              
              <div className="space-y-4">
                {syncLocations.map((location) => {
                  const Icon = location.icon;
                  return (
                    <div key={location.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        <Icon className="w-8 h-8 text-blue-600 mr-3" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{location.name}</p>
                          <p className="text-sm text-gray-500">
                            {location.files.toLocaleString()} fișiere • {location.size}
                          </p>
                          {location.warning && (
                            <p className="text-xs text-yellow-600">{location.warning}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          location.status === 'connected' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                          location.status === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}>
                          {location.status}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">{location.lastSync}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Job-uri de Backup
              </h3>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
                + Nou Job
              </button>
            </div>

            <div className="space-y-4">
              {backupJobs.map((job) => (
                <div key={job.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg ${getStatusColor(job.status)} mr-4`}>
                        {getStatusIcon(job.status)}
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {job.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {job.schedule}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getBackupTypeColor(job.type)}`}>
                        {job.type.toUpperCase()}
                      </span>
                      <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {job.progress && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                        <span>Progres backup</span>
                        <span>{job.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${job.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-1">Ultima rulare</p>
                      <p className="font-medium text-gray-900 dark:text-white">{job.lastRun}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Următoarea rulare</p>
                      <p className="font-medium text-gray-900 dark:text-white">{job.nextRun}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Dimensiune</p>
                      <p className="font-medium text-gray-900 dark:text-white">{job.size}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Durată</p>
                      <p className="font-medium text-gray-900 dark:text-white">{job.duration}</p>
                    </div>
                  </div>

                  {job.error && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-center">
                        <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                        <p className="text-sm text-red-700 dark:text-red-300">{job.error}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-2 mt-4">
                    <button className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                      <Eye className="w-4 h-4 mr-1 inline" />
                      Vezi
                    </button>
                    <button className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
                      <Play className="w-4 h-4 mr-1 inline" />
                      Rulează
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'sync' && (
          <div className="space-y-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Locații de Sincronizare
                </h3>
                <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sincronizare Manuală
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {syncLocations.map((location) => {
                  const Icon = location.icon;
                  return (
                    <div key={location.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl mr-3">
                            <Icon className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {location.name}
                            </h4>
                            <p className="text-sm text-gray-500">{location.region}</p>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          location.status === 'connected' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                          location.status === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}>
                          {location.status}
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Fișiere:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {location.files.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Dimensiune:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{location.size}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Ultima sincronizare:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{location.lastSync}</span>
                        </div>
                      </div>

                      {location.warning && (
                        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <div className="flex items-center">
                            <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
                            <p className="text-sm text-yellow-700 dark:text-yellow-300">{location.warning}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end space-x-2 mt-6">
                        <button className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <Settings className="w-4 h-4 mr-1 inline" />
                          Configurează
                        </button>
                        <button className="px-3 py-1 text-sm text-green-600 hover:text-green-700 border border-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                          <RefreshCw className="w-4 h-4 mr-1 inline" />
                          Sincronizează
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Backup Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Setări Backup
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Interval backup automat
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    <option>La fiecare oră</option>
                    <option>La fiecare 4 ore</option>
                    <option>Zilnic</option>
                    <option>Săptămânal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Politica de retenție
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    <option>30 de zile</option>
                    <option>90 de zile</option>
                    <option>6 luni</option>
                    <option>1 an</option>
                    <option>Permanent</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      defaultChecked
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Compresie backup-uri
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      defaultChecked
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Criptare backup-uri
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Verificare integritate automată
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Notificări
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      defaultChecked
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Email la backup finalizat cu succes
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      defaultChecked
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Email la erori de backup
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      Notificări push mobile
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Adresă email pentru notificări
                  </label>
                  <input
                    type="email"
                    placeholder="admin@company.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rapoarte programate
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    <option>Dezactivate</option>
                    <option>Zilnic</option>
                    <option>Săptămânal</option>
                    <option>Lunar</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CloudBackup;